<script setup lang="ts">
/**
 * App Header Component
 *
 * Main application header with navigation and user menu
 */
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import { MenuIcon, ChevronDown, LogOut, User } from 'lucide-vue-next';
import searchIcon from '@/assets/search.svg';
import darkModeIcon from '@/assets/dark_mode.svg';
import { useAuth } from '@/modules/auth';
import { useUIStore } from '@/stores';
import { useNotificationsStore } from '@/stores/notifications.store';
import logoImage from '@/assets/timeless-medical-logo.png';
import notificationIcon from '@/assets/notifications_unread.svg';
import { ref, computed } from 'vue';
import NotificationPanel from '@/shared/components/notifications/NotificationPanel.vue';
import NotificationDetailModal from '@/shared/components/notifications/NotificationDetailModal.vue';

const { user, logout } = useAuth();
const uiStore = useUIStore();
const notificationsStore = useNotificationsStore();

const userInitials = computed(() => {
  const name = user.value?.name || '';
  if (!name.trim()) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
});

// Search state
const isSearchExpanded = ref(false);
const searchQuery = ref('');

function toggleSearch() {
  isSearchExpanded.value = !isSearchExpanded.value;
  if (!isSearchExpanded.value) {
    searchQuery.value = '';
  }
}


function toggleDesktopSidebar() {
  uiStore.toggleSidebarCollapse();
}

function toggleSidebar() {
  uiStore.toggleSidebar();
}
</script>

<template>
  <header class="custom-header sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-4">
    <!-- Mobile menu button -->
    <button
      type="button"
      class="-m-2.5 p-2.5 text-gray-700 lg:hidden"
      @click="toggleSidebar"
    >
      <span class="sr-only">Open sidebar</span>
      <MenuIcon
        class="h-6 w-6"
        aria-hidden="true"
      />
    </button>

    <!-- Desktop sidebar toggle button -->
    <button
      type="button"
      class="hidden lg:flex -m-2.5 p-2.5 text-gray-700 hover:text-primary-600 transition-colors items-center"
      :title="uiStore.sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'"
      @click="toggleDesktopSidebar"
    >
      <MenuIcon
        class="h-6 w-6"
        aria-hidden="true"
      />
    </button>

    <!-- Logo -->
    <RouterLink
      to="/"
      class="flex items-center"
    >
      <img
        :src="logoImage"
        alt="Timeless Medical"
        class="h-8 w-auto"
      >
    </RouterLink>

    <!-- Separator -->
    <!-- <div class="h-6 w-px bg-gray-200 lg:hidden dark:bg-gray-700" aria-hidden="true" /> -->

    <div class="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
      <!-- Search (placeholder) -->
      <div class="flex flex-1" />

      <!-- User menu -->
      <div class="flex items-center gap-x-4 lg:gap-x-4">
        <!-- Expandable Search (future scope) -->
        <!-- <div class="flex items-center gap-x-2">
          <button
            type="button"
            class="flex items-center rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            :title="isSearchExpanded ? 'Close search' : 'Search'"
            @click="toggleSearch"
          >
            <img
              :src="searchIcon"
              alt="Search"
              class="h-5 w-5"
            >
          </button>
          <transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="w-0 opacity-0"
            enter-to-class="w-64 opacity-100"
            leave-active-class="transition-all duration-300 ease-in"
            leave-from-class="w-64 opacity-100"
            leave-to-class="w-0 opacity-0"
          >
            <input
              v-if="isSearchExpanded"
              v-model="searchQuery"
              type="text"
              placeholder="Search..."
              class="block rounded-lg border border-gray-300 bg-white py-2 px-3 text-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-primary-400 dark:focus:ring-primary-400"
              autofocus
            >
          </transition>
        </div> -->

        <!-- Moon Icon (future scope) -->
        <!-- <button
          type="button"
          class="flex items-center rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          title="Theme"
        >
          <img
            :src="darkModeIcon"
            alt="Theme"
            class="h-5 w-5"
          >
        </button> -->


        <!-- Language indicator (future scope) -->
        <!-- <div class="flex items-center gap-x-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <img
            src="https://flagcdn.com/w40/us.png"
            alt="English (US)"
            class="h-5 w-7 object-cover rounded"
          >
          <span class="hidden lg:inline">EN-US</span>
        </div> -->

        <!-- Notification Icon -->
        <div class="relative">
          <button
            type="button"
            title="Notifications"
            class="relative flex items-center rounded-lg p-2 hover:bg-gray-100 transition-colors"
            @click.stop="notificationsStore.togglePanel()"
          >
            <img
              :src="notificationIcon"
              alt="Notifications"
              class="h-6 w-6"
            >
            <span
              v-if="notificationsStore.pendingCount > 0"
              class="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white"
            >
              {{ notificationsStore.pendingCount > 9 ? '9+' : notificationsStore.pendingCount }}
            </span>
          </button>
          <NotificationPanel />
        </div>

        <!-- User Profile Display -->
        <Menu
          v-if="user?.name"
          as="div"
          class="relative"
        >
          <MenuButton class="flex items-center gap-x-2.5 rounded-lg px-2.5 py-1.5 hover:bg-gray-100 transition-colors cursor-pointer">
            <span class="flex items-center justify-center h-7 w-7 rounded-full bg-blue-600 text-white text-xs font-semibold shadow-sm">
              {{ userInitials }}
            </span>
            <span class="text-sm font-semibold text-gray-900">
              {{ user.name }}
            </span>
          </MenuButton>
          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <MenuItems class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <MenuItem v-slot="{ active }">
                <RouterLink
                  to="/my-profile"
                  :class="[
                    active ? 'bg-gray-100' : '',
                    'flex w-full items-center gap-x-2 px-4 py-2 text-sm text-gray-700'
                  ]"
                >
                  <User class="h-4 w-4" aria-hidden="true" />
                  My Profile
                </RouterLink>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <button
                  type="button"
                  :class="[
                    active ? 'bg-gray-100' : '',
                    'flex w-full items-center gap-x-2 px-4 py-2 text-sm text-gray-700'
                  ]"
                  @click="logout"
                >
                  <LogOut class="h-4 w-4" aria-hidden="true" />
                  Sign out
                </button>
              </MenuItem>
            </MenuItems>
          </transition>
        </Menu>
      </div>
    </div>
  </header>
  <!-- Notification Detail Modal (teleported to body) -->
  <NotificationDetailModal />
</template>

<style scoped>
.custom-header {
  background: linear-gradient(0deg, rgba(228, 247, 251, 0.20) 100%, rgba(255, 255, 255, 0.20) 100%, rgba(228, 247, 251, 0.20) 100%), white;
}

.cheron-down {
  color: var(--color-gray-900);
}
</style>
